import { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

import { colors, strCode } from "../../../consts";
import { t } from "../../../services";

import {
  DropdownInput,
  FlexContainers,
  Modal,
  SmallButton,
  TextButton,
  WrapperContainer
} from "../../../components";

const HardwareLocationModal = ({ locations, hardware, loading, active, setActive, onSave }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const locationOptions = useMemo(() => {
    return locations.map((el) => {
      return { id: el.id, value: el?.locationName };
    });
  }, [locations]);

  const isSameData = useMemo(() => {
    const locationId = hardware?.locationId ?? null;
    const selectedLocationId = selectedLocation?.id ?? null;

    return locationId === selectedLocationId;
  }, [selectedLocation, hardware]);

  useEffect(() => {
    if (hardware && locationOptions && !isSubmitted) {
      const locationId = hardware?.locationId ?? null;

      if (locationId) {
        setSelectedLocation(locationOptions.find((location) => location.id === locationId));
      }
    }
  }, [hardware, locationOptions, isSubmitted]);

  useEffect(() => {
    if (isSubmitted && !loading && active) {
      onClose();
    }
  }, [isSubmitted, loading, active]);

  const handleSave = async () => {
    setIsSubmitted(true);
    await onSave(selectedLocation);
  };

  const onClose = () => {
    setActive(false);
    setIsSubmitted(false);
    setSelectedLocation(null);
  };

  return (
    <Modal
      id={"hardware-location-modal"}
      active={active}
      closable={false}
      title={t(strCode.ASSIGN_HARDWARE_ID, { id: hardware?.hubId?.slice(-8) })}
      preventOverflow>
      <WrapperContainer Container={FlexContainers.FlexColumnStart} gap={24} width={600}>
        <DropdownInput
          id="hardware-location-selector"
          title={t(strCode.SELECT_HARDWARE_LOCATION_TITLE)}
          placeholder={t(strCode.LOCATION_TITLE)}
          options={locationOptions}
          selectedOption={selectedLocation}
          onSelectOption={(id) => {
            setSelectedLocation(locationOptions.find((el) => el.id === id));
          }}
          isSearchable
        />
        <WrapperContainer
          Container={selectedLocation ? FlexContainers.FlexRowBetween : FlexContainers.FlexRowEnd}
          gap={8}
          width={"100%"}>
          {!!selectedLocation && (
            <TextButton
              color={colors.ERROR}
              onClick={() => setSelectedLocation(null)}
              disabled={loading}>
              {t(strCode.UNASSIGN_BTN)}
            </TextButton>
          )}

          <WrapperContainer Container={FlexContainers.FlexRowStart} gap={8}>
            <SmallButton secondary onClick={onClose} disabled={loading}>
              {t(strCode.CANCEL_BTN)}
            </SmallButton>
            <SmallButton onClick={handleSave} disabled={isSameData || loading} loading={loading}>
              {t(strCode.SAVE_BTN)}
            </SmallButton>
          </WrapperContainer>
        </WrapperContainer>
      </WrapperContainer>
    </Modal>
  );
};

HardwareLocationModal.propTypes = {
  hardware: PropTypes.shape({
    hubId: PropTypes.string.isRequired,
    locationId: PropTypes.string
  }),
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      locationName: PropTypes.string
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  active: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired
};

export default HardwareLocationModal;
